import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Search, Variable } from "lucide-react";

import { useTemplateVariables } from "hooks/useTemplateVariables";

import styles from "./TemplateVariablePicker.module.css";

const CATEGORY_ORDER = [
  "Patient",
  "Address",
  "Doctor",
  "Clinical",
  "Today",
  "Medication",
  "Consultation",
];

const CategorySection = ({ category, variables, onInsert }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className={styles.category}>
      <button
        type="button"
        className={styles.categoryHeader}
        onClick={() => setIsExpanded((previous) => !previous)}
      >
        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className={styles.categoryName}>{category}</span>
        <span className={styles.categoryCount}>{variables.length}</span>
      </button>

      {isExpanded && (
        <ul className={styles.variableList}>
          {variables.map((variable) => (
            <li key={variable.variable}>
              <button
                type="button"
                className={styles.variableItem}
                onClick={() => onInsert(variable.placeholder)}
                title={variable.placeholder}
              >
                <span className={styles.variableDescription}>
                  {variable.description}
                </span>
                <span className={styles.variablePlaceholder}>
                  {variable.placeholder}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const TemplateVariablePicker = ({ onInsert }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const { data: variables = [], isLoading } = useTemplateVariables();

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const groupedVariables = useMemo(() => {
    const filtered = search
      ? variables.filter(
          (variable) =>
            variable.description.toLowerCase().includes(search.toLowerCase()) ||
            variable.placeholder.toLowerCase().includes(search.toLowerCase())
        )
      : variables;

    const groups = {};

    for (const variable of filtered) {
      const category = variable.category || "Other";

      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(variable);
    }

    const known = CATEGORY_ORDER.filter((category) => groups[category]);
    const extras = Object.keys(groups)
      .filter((category) => !CATEGORY_ORDER.includes(category))
      .sort();

    return [...known, ...extras].map((category) => ({
      category,
      variables: groups[category],
    }));
  }, [variables, search]);

  const handleInsert = (placeholder) => {
    onInsert(placeholder);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div className={styles.container}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.triggerButton}
        onClick={() => setIsOpen((previous) => !previous)}
      >
        <Variable size={16} />
        Insert Variable
      </button>

      {isOpen && (
        <div ref={dropdownRef} className={styles.dropdown}>
          <div className={styles.searchWrapper}>
            <Search size={16} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search variables..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              autoFocus
            />
          </div>

          <div className={styles.variablesContainer}>
            {isLoading ? (
              <div className={styles.emptyState}>Loading variables...</div>
            ) : groupedVariables.length === 0 ? (
              <div className={styles.emptyState}>No variables found</div>
            ) : (
              groupedVariables.map(({ category, variables: categoryVariables }) => (
                <CategorySection
                  key={category}
                  category={category}
                  variables={categoryVariables}
                  onInsert={handleInsert}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
